import { useCallback, useEffect, useRef, useState } from 'react';
import {
  notificationListSchema,
  type Notification,
  type NotificationKind,
} from '@forecastify/shared';
import type { IconName, Notification as ShellNotification } from '@forecastify/ui';
import { useI18n, type Translate } from '../../i18n';
import type { Locale } from '../../i18n/catalogue';
import { useLive } from '../../live/live-provider';
import { useLiveInvalidation } from '../../live/use-live';
import { NOTIFICATION_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { relativeTime } from '../relative-time';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The bell, filled in. C17.                                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `AppShell` has drawn the popover since C2 and `app.tsx` has been passing it `notifications={[]}`
 * with a comment saying C17 owns the other half. This is that half: one hook, whose output is
 * exactly the shell's prop.
 *
 * ⚠️⚠️ **THE SENTENCES ARE COMPOSED HERE, NOT STORED.** `0025` keeps `kind`, an actor reference and a
 * `detail` string; the row's title comes out of the catalogue in the reader's language. The mockup
 * stores `t: 'Farkas András hozzárendelt egy feladathoz'` in its seed data — a Hungarian sentence in
 * the database, which rule 7 does not allow and which would freeze the language at the moment the
 * notification was raised.
 *
 * ⚠️ **IT USED TO BE A POLL, AND C21 REPLACED IT.** The note here said a stream *"wants server
 * infrastructure nobody has decided on"*; `decisions.md` R-19 decided it, and this was the first
 * consumer — the shortest end-to-end proof of the whole path, and one request per minute per open
 * tab removed.
 *
 * ⚠⚠️ **THE POLL IS STILL HERE, AS THE FALLBACK, AND THAT IS DELIBERATE.** Every default in a
 * reverse proxy is wrong for SSE and **each one fails without an error anywhere** —
 * `self-hosted-deployment.md` has a table of them under a heading that reads *"getting it wrong is
 * SILENT"*. A deployment whose proxy buffers would otherwise ship a bell that never updates at all,
 * which is strictly worse than the minute-old one it replaced. So the interval runs only while the
 * stream is not connected, and the doc's promise — *"if you cannot configure the proxy, the product
 * still works"* — stays true.
 */

/** How often the badge re-reads **when the stream is not connected**. See the note above. */
export const NOTIFICATION_POLL_MS = 60_000;

/**
 * ⚠️ **The mockup's own icons** (`main.jsx` 118–123): `at-sign` for a mention, `message-square` for a
 * comment, `users` for an assignment, `circle-check` for a status move. Kept because they are
 * distinguishable at 13px, which is the whole job of an icon in a 368px popover.
 */
const ICONS: Record<NotificationKind, IconName> = {
  mentioned: 'at-sign',
  commented: 'message-square',
  assigned: 'users',
  status_changed: 'circle-check',
  /* ⚠️ `clock`, which the mockup does not assign — it draws no `due_soon` row, because its seed
   * data has none. Of the closed icon set it is the only glyph that reads as "time is passing"
   * rather than as an action somebody took, which is exactly what separates this kind from the
   * other four. Flagged as ours rather than transcribed. */
  due_soon: 'clock',
  /* `0182` · a leave request and an overtime claim. The calendar and the timer for what arrived,
     the tick for a decision, the cross for anything withdrawn or cancelled. */
  leave_requested: 'calendar-days',
  leave_decided: 'circle-check',
  leave_withdrawn: 'x',
  leave_revoked: 'x',
  leave_cancelled: 'x',
  overtime_requested: 'timer',
  overtime_decided: 'circle-check',
  overtime_withdrawn: 'x',
  /* `0185` · contracts and projects. The document for a contract's own news, the warning for a
     frame or a date running out, the folder for a project, the people for a membership. */
  contract_ending: 'calendar',
  contract_ended_open: 'triangle-alert',
  contract_budget: 'triangle-alert',
  contract_burn_early: 'triangle-alert',
  contract_owner: 'file-text',
  project_risk_worsened: 'triangle-alert',
  project_added: 'users',
  project_pm: 'user-round',
  project_closed: 'folder',
};

/**
 * The sentence.
 *
 * ⚠️⚠️ **TWO KEYS PER KIND — WITH AN ACTOR AND WITHOUT — AND `t()` IS CALLED WITH A LITERAL IN EVERY
 * BRANCH.** `t(keyFor(kind))` is invisible to any check that reads literal arguments, and the fix
 * somebody reaches for when an entry reports as an orphan is deleting the entry. Eight `if`s is the
 * price of both halves being findable.
 *
 * ⚠️ **The actor's name leads the clause and takes no suffix.** Hungarian inflects by vowel harmony,
 * so `{actor}-tól` is wrong for half of all names; the nominative-plus-verb shape the mockup uses
 * (*"Farkas András hozzárendelt egy feladathoz"*) is correct for every name. The actorless variants
 * are not a fallback for a missing translation — they are what a departed colleague's notification
 * says, since `0025` sets the actor to null rather than deleting the row.
 */
export function notificationTitle(t: Translate, entry: Notification): string {
  const actor = entry.actorName;
  const who = actor ?? t('notif.someone');
  /* `0182` · the request kinds. A decided row's `detail` is the decision itself. */
  const rejected = entry.detail === 'rejected';
  switch (entry.kind) {
    case 'leave_requested':
      return t('notif.leave.requested', { actor: who });
    case 'leave_decided':
      return t(rejected ? 'notif.leave.rejected' : 'notif.leave.approved', { actor: who });
    case 'leave_withdrawn':
      return t('notif.leave.withdrawn', { actor: who });
    case 'leave_revoked':
      return t('notif.leave.revoked', { actor: who });
    case 'leave_cancelled':
      return t('notif.leave.cancelled', { actor: who });
    case 'overtime_requested':
      return t('notif.overtime.requested', { actor: who });
    case 'overtime_decided':
      return t(rejected ? 'notif.overtime.rejected' : 'notif.overtime.approved', { actor: who });
    case 'overtime_withdrawn':
      return t('notif.overtime.withdrawn', { actor: who });
    /* `0185` · contracts and projects. The subject's name is the second line, so the title is the
       event alone; `detail` carries the figure. */
    case 'contract_ending':
      return t('notif.contract.ending', { days: entry.detail ?? '' });
    case 'contract_ended_open':
      return t('notif.contract.endedOpen');
    case 'contract_budget':
      return entry.detail === '100'
        ? t('notif.contract.budgetSpent')
        : t('notif.contract.budget', { percent: entry.detail ?? '' });
    case 'contract_burn_early':
      return t('notif.contract.burnEarly');
    case 'contract_owner':
      return t('notif.contract.owner', { actor: who });
    case 'project_risk_worsened':
      return t('notif.project.riskWorsened');
    case 'project_added':
      return t('notif.project.added', { actor: who });
    case 'project_pm':
      return t('notif.project.pm', { actor: who });
    case 'project_closed':
      return t('notif.project.closed', { actor: who });
    default:
      break;
  }
  if (entry.kind === 'mentioned') {
    return actor === null ? t('notif.mentioned') : t('notif.mentioned.by', { actor });
  }
  if (entry.kind === 'commented') {
    return actor === null ? t('notif.commented') : t('notif.commented.by', { actor });
  }
  if (entry.kind === 'assigned') {
    return actor === null ? t('notif.assigned') : t('notif.assigned.by', { actor });
  }
  /**
   * ⚠⚠️ **ONE KEY, NOT TWO, AND THAT IS THE POINT OF THIS KIND.** Every branch above has an
   * actorless variant for the case where a colleague has left — `0025` nulls the actor rather than
   * deleting the row. `due_soon` has NO actor by construction: `0029`'s sweep is time, not a person,
   * and an *"X figyelmeztetett a határidőre"* variant would be a sentence nothing can ever produce.
   */
  if (entry.kind === 'due_soon') return t('notif.dueSoon');
  return actor === null ? t('notif.status') : t('notif.status.by', { actor });
}

/**
 * `KEY · what happened`, which is the mockup's second line.
 *
 * ⚠️ **`detail` wins over the title**, because for three of the four kinds it is the thing that
 * changed — a comment excerpt or `Aktív → Felfüggesztve`. An assignment has no detail, so it shows
 * what it is about instead.
 */
function description(entry: Notification): string | undefined {
  /* A request row is named by its identifier — `lr_07bb0567` — the name the queue shows (R-57). */
  if (entry.requestCode !== null) return entry.requestCode;
  /* `0186` · a contract by its identifier and name (R-57), a project by its name. */
  if (entry.contractCode !== null) {
    return entry.contractName === null
      ? entry.contractCode
      : `${entry.contractCode} · ${entry.contractName}`;
  }
  if (entry.projectName !== null) return entry.projectName;
  const what = entry.detail ?? entry.taskTitle;
  if (entry.taskKey === null) return what ?? undefined;
  return what === null ? entry.taskKey : `${entry.taskKey} · ${what}`;
}

/**
 * When.
 *
 * ⚠️ **`relative-time.ts` does the work** since item 12 — the devices and passkeys lists needed the
 * same sentence, and two copies of a locale-aware formatter is how two surfaces come to disagree
 * about what *"yesterday"* is called.
 */
export function notificationTime(iso: string, locale: Locale, now: number = Date.now()): string {
  return relativeTime(iso, locale, now);
}

export interface NotificationsHandle {
  /** Straight into `AppShell`'s `notifications` prop. */
  readonly notifications: readonly ShellNotification[];
  /** 30 Oct 2026 · the server's count of every unread one; `undefined` until the first answer. */
  readonly unreadCount: number | undefined;
  readonly onMarkAllRead: () => void;
}

/**
 * @param onOpenTask what to do when a row with a task is clicked — navigation belongs to the caller,
 * which is the one place that holds the router.
 */
export function useNotifications(
  onOpenTask: (key: string) => void,
  onOpenRequest: (id: string) => void,
  /** `0186` · a project or a contract — an address inside the workspace, `/projects/…`. */
  onOpenPath: (path: string) => void = () => undefined,
): NotificationsHandle {
  const session = useSession();
  const { t, locale } = useI18n();
  const [entries, setEntries] = useState<readonly Notification[]>([]);
  /* 30 Oct 2026 · the server's count of EVERY unread one — `entries` is the newest page only. */
  const [unread, setUnread] = useState<number | undefined>(undefined);

  /**
   * ⚠️ **A ref, so the poll and the mark-read handler always see the current callback** without the
   * effect below re-subscribing on every render. `onOpenTask` is an inline arrow at the call site —
   * putting it in the effect's dependency list would tear down and rebuild the interval on each
   * render of the shell.
   */
  const open = useRef(onOpenTask);
  open.current = onOpenTask;
  const openRequest = useRef(onOpenRequest);
  openRequest.current = onOpenRequest;
  const openPath = useRef(onOpenPath);
  openPath.current = onOpenPath;

  const load = useCallback(async (): Promise<void> => {
    const response = await session.request('/notifications').catch(() => null);
    /**
     * ⚠️⚠️ **A FAILURE IS SILENT HERE, AND IT IS THE ONE PLACE THAT IS RIGHT.** `state-rules.md` wants
     * errors surfaced — but this request is made every minute by a component that wraps every screen,
     * so a toast on failure would put "could not load notifications" over somebody's work every sixty
     * seconds while the API is restarting. The bell keeps whatever it last had, which is the honest
     * rendering of "we do not know yet".
     */
    if (response?.ok !== true) return;

    const parsed = notificationListSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) return;
    setEntries(parsed.data.data);
    setUnread(parsed.data.meta.unread);
  }, [session]);

  const stream = useLive();
  const connected = stream?.connected ?? false;

  useEffect(() => {
    /* ⚠️ A mutable object rather than a `let`, which TypeScript narrows to its initialiser and then
     * reports the guard as provably dead — `hard-won.md` C15-3. */
    const alive = { value: true };
    void load();

    /* ⚠️ **Only while the stream is down.** With it up the bell is driven by
     * `useLiveInvalidation` below and a timer would be a second, slower source of the same truth. */
    if (connected) {
      return () => {
        alive.value = false;
      };
    }

    const timer = setInterval(() => {
      if (alive.value) void load();
    }, NOTIFICATION_POLL_MS);
    return () => {
      alive.value = false;
      clearInterval(timer);
    };
  }, [load, connected]);

  /* ⚠️ A notification is raised by somebody else's action — an assignment, a comment, a mention —
   * so this is the one surface where a live update is the feature rather than a nicety. */
  useLiveInvalidation(NOTIFICATION_TOPICS, load);

  const markRead = (id: string): void => {
    /* ⚠️ Optimistic, then reconciled — `state-rules.md` §7. A revert on failure would re-light a dot
     * on the row somebody just opened, which is worse than being briefly wrong: the reload the next
     * poll performs corrects it either way. */
    setEntries((current) =>
      current.map((entry) =>
        entry.id === id ? { ...entry, readAt: new Date().toISOString() } : entry,
      ),
    );
    setUnread((count) => (count === undefined ? count : Math.max(0, count - 1)));
    void session
      .request(`/notifications/${id}/read`, { method: 'POST' })
      .then(() => load())
      .catch(() => undefined);
  };

  return {
    unreadCount: unread,
    notifications: entries.map((entry) => ({
      id: entry.id,
      title: notificationTitle(t, entry),
      description: description(entry),
      time: notificationTime(entry.createdAt, locale),
      icon: ICONS[entry.kind],
      unread: entry.readAt === null,
      onSelect: () => {
        /* ⚠️ Read first, then navigate. The other order loses the flip whenever the target route
         * unmounts the shell — which it does not today, and which is not worth depending on. */
        if (entry.readAt === null) markRead(entry.id);
        if (entry.taskKey !== null) open.current(entry.taskKey);
        else if (entry.requestId !== null) openRequest.current(entry.requestId);
        else if (entry.contractCode !== null) {
          openPath.current(`/contracts/${encodeURIComponent(entry.contractCode)}`);
        } else if (entry.projectId !== null) openPath.current(`/projects/${entry.projectId}`);
      },
    })),

    onMarkAllRead: () => {
      const stamp = new Date().toISOString();
      setEntries((current) =>
        current.map((entry) => (entry.readAt === null ? { ...entry, readAt: stamp } : entry)),
      );
      setUnread(0);
      void session
        .request('/notifications/read-all', { method: 'POST' })
        .then(() => load())
        .catch(() => undefined);
    },
  };
}
